import { useState, useEffect, useMemo, useCallback } from 'react';
import { useEconomyMode } from '@/contexts/EconomyModeContext';
import { POLLING_INTERVALS, ECONOMY_MODE_CONFIG } from '@/lib/performanceConfig';

export type QueryWeight = 'A' | 'B' | 'C';

interface UseSmartPollingOptions {
  weight: QueryWeight;
  baseInterval?: number;
  enabled?: boolean;
}

/**
 * Hook que retorna o refetchInterval correto baseado no peso, visibilidade e Economy Mode.
 * 
 * Regras:
 * - Tab invisível → false (pausa)
 * - Economy Mode ativo:
 *   - PESO A: mantém normal
 *   - PESO B: dobra intervalo
 *   - PESO C: false (pausa total)
 * - Jitter: 0-15s aleatório para evitar sincronização
 */
export function useSmartPolling({ weight, baseInterval, enabled = true }: UseSmartPollingOptions) {
  const { isEconomyMode, reportQueryFailure, reportQuerySuccess } = useEconomyMode();
  const [isVisible, setIsVisible] = useState(() => typeof document !== 'undefined' ? document.visibilityState === 'visible' : true);

  useEffect(() => {
    const handler = () => setIsVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  // Jitter fixo por instância do hook (não muda a cada render)
  const jitter = useMemo(() => Math.random() * ECONOMY_MODE_CONFIG.JITTER_MAX_MS, []);

  const minInterval = POLLING_INTERVALS[`PESO_${weight}`];
  const effectiveBase = Math.max(baseInterval || minInterval, minInterval);

  const refetchInterval = useMemo((): number | false => {
    if (!enabled || !isVisible) return false;

    if (isEconomyMode) {
      switch (weight) {
        case 'A': return effectiveBase + jitter;
        case 'B': return effectiveBase * 2 + jitter;
        case 'C': return false; // Pausa total
      }
    }

    return effectiveBase + jitter;
  }, [enabled, isVisible, isEconomyMode, weight, effectiveBase, jitter]);

  // Wrapper para onError/onSuccess das queries
  const onQueryError = useCallback((error: unknown) => {
    reportQueryFailure(error);
  }, [reportQueryFailure]);

  const onQuerySuccess = useCallback(() => {
    reportQuerySuccess();
  }, [reportQuerySuccess]);

  return {
    refetchInterval,
    isEconomyMode,
    isVisible,
    onQueryError,
    onQuerySuccess,
  };
}
